<template>
    <div class="auth__selfregistration_form">
        <div class="container">
            <div class="columns is-variable">
                <div class="column">
                    <div class="auth__selfregistration_form_box">
                        <div class="auth__selfregistration_form_box_fields">
                            <form-text
                                :label="'First name'"
                                :name="'auth-self-registration-form-first-name'"
                                :v-validate="'required'"
                                :required="true"
                                :data-vv-as="'first name'"
                                v-model="firstName"
                            />
                            <form-text
                                :label="'Last name'"
                                :name="'auth-self-registration-form-last-name'"
                                :v-validate="'required'"
                                :required="true"
                                :data-vv-as="'last name'"
                                v-model="lastName"
                            />
                            <form-email
                                :label="'Email address'"
                                :name="'auth-self-registration-form-email-address'"
                                :v-validate="{ required: true, email: true }"
                                :required="true"
                                :data-vv-as="'email address'"
                                v-model="emailAddress"
                            />
                            <form-text
                                ref="newpasswordchallenge"
                                :password="true"
                                :label="'Password'"
                                :name="'auth-self-registration-form-password'"
                                :v-validate="{ required: true, regex: passwordRegex }"
                                :data-vv-as="'password'"
                                v-model="password"
                            >Password length must be at least 8 and includes:<br>- lowercase letter<br>- uppercase letter<br>- special character<br>- number
                            </form-text>
                            <form-text
                                ref="confirmnewpasswordchallenge"
                                :password="true"
                                :label="'Confirm password'"
                                :name="'auth-self-registration-form-confirm-password'"
                                :v-validate="{ required: true, regex: passwordRegex, is: password }"
                                :data-vv-as="'confirm password'"
                                v-model="passwordConfirm"
                            />
                            <form-select-org-units
                                v-if="orgUnitsList.length"
                                :multiple="false"
                                :label="'Organisation unit'"
                                :name="'auth-self-registration-form-org-unit'"
                                :required="true"
                                :v-validate="{ required: orgUnitsList.length }"
                                :data-vv-as="'organisation unit'"
                                :action-active="false"
                                :items="orgUnitsList"
                                :allowed="keyOrganisationUnitsList"
                                :check-parent-if-children-selected="false"
                                v-model="orgUnit"
                            ></form-select-org-units>
                            <form-select
                                v-if="groupsList.length"
                                :multiple="true"
                                :label="'Groups'"
                                :items="groupsList"
                                :name="'auth-self-registration-form-groups'"
                                v-model="groups"
                            ></form-select>
                            <component-custom-fields-handler
                                :fieldset="workspaceCustomFields"
                                :values="customFields"
                                @input="setCustomField"
                            ></component-custom-fields-handler>
                        </div>
                        <transition name="fade" mode="out-in">
                            <template v-if="error">
                                <div class="auth__selfregistration_form_box_error">
                                    <component-note :danger="true">{{ error }}</component-note>
                                </div>
                            </template>
                        </transition>
                        <div class="auth__selfregistration_form_box_actions">
                            <component-button
                                ref="selfregistrationsubmit"
                                :theme="'primary'"
                                :size="'full'"
                                :animate-loading="true"
                                :animate-loading-event-name="'user-self-registration'"
                                :name="'auth-self-registration-form-box-actions-self-registration'"
                                @click="openTermsAndConditionsNotify"
                            >Sign up</component-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            ref="termsandconditionsnotify"
            :title-text="'Terms and conditions'"
            :content-text="''"
            :size="'medium'"
            :cancel-button-text="'Back'"
            :submit-button-text="'Accept'"
            :submit-button-animate-loading="true"
            :close-button-active="false"
            :overlay-prevent-close="true"
            :prevent-close="true"
            @emit-submit="handleSelfRegistration"
        >
            <component-text
                :html="true"
                :is-slot="true"
            >
                <p>Welcome to Form LMS. <br>Use of our site constitutes acceptance of our <a href="https://www.melearning.co.uk/terms-and-conditions-lms" target="_blank">terms and conditions of use</a> and our <a href="https://www.melearning.co.uk/privacy-and-cookie-policy" target="_blank">privacy and cookie policy</a> and your agreement to be bound by them.</p>
            </component-text>
        </pop-notify>
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentNote from '@/components/default/shared/ComponentNote'
import { passwordRegex } from '@/variables'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'
import FormSelect from '@/components/default/forms/FormSelect'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentText from '@/components/default/shared/ComponentText'
import ComponentCustomFieldsHandler from '@/components/default/shared/ComponentCustomFieldsHandler'
import FormEmail from '@/components/default/forms/FormEmail'

export default {
  name: 'auth-self-registration-form',
  components: { FormEmail, ComponentCustomFieldsHandler, ComponentText, PopNotify, FormSelect, FormSelectOrgUnits, ComponentNote, ComponentButton, FormText },
  data () {
    return {
      passwordRegex
    }
  },
  computed: {
    firstName: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationFirstName
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationFirstName', value)
      }
    },
    lastName: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationLastName
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationLastName', value)
      }
    },
    emailAddress: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationEmailAddress
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationEmailAddress', value)
      }
    },
    password: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationPassword
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationPassword', value)
      }
    },
    passwordConfirm: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationPasswordConfirm
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationPasswordConfirm', value)
      }
    },
    orgUnit: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationOrgUnit
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationOrgUnit', value)
      }
    },
    groups: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationGroups
      },
      set (value) {
        this.$store.dispatch('authUserSetSelfRegistrationGroups', value)
      }
    },
    customFields: {
      get () {
        return this.$store.getters.getAuthUserSelfRegistrationCustomFields
      }
    },
    error () {
      return this.$store.getters.getAuthUserValidateKeyError
    },
    keyOrganisationUnitsAvailableFor: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyOrgUnitsAvailableFor
      }
    },
    keyOrganisationUnitsList: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyOrgUnitsList
      }
    },
    keyGroupsAvailableFor: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyGroupsAvailableFor
      }
    },
    keyGroupsList: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyGroupsList
      }
    },
    keyWorkspaceId: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceRegisterKeyWorkspaceId
      }
    },
    orgUnitsList () {
      return this.$store.getters.getLmsSuperadminOrgUnitsChildren
    },
    groupsList () {
      const rawGroupsList = this.$store.getters.getLmsSuperadminGroupsMappedLabelValue
      const allowedGroups = this.keyGroupsList
      const groupsOutput = allowedGroups ? rawGroupsList.filter(obj => allowedGroups.includes(obj.value)) : rawGroupsList

      return groupsOutput
    },
    workspaceCustomFields () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldsData
    }
  },
  methods: {
    setCustomField (value) {
      this.$store.dispatch('authUserSetSelfRegistrationCustomField', value)
    },
    openTermsAndConditionsNotify () {
      this.$validator.resume()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$refs.termsandconditionsnotify.openNotify()
        }
      })
      this.$eventBus.$emit('stop-user-self-registration')
    },
    handleSelfRegistration () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.performSelfRegistration()
        } else {
          this.$eventBus.$emit('stop-user-self-registration')
        }
      })
    },
    performSelfRegistration () {
      this.$store.dispatch('authUserSelfRegistration').then(response => {
        this.$router.push({ name: 'auth-complete-registration' })
      }).finally(() => {
        this.$eventBus.$emit('stop-user-self-registration')
        this.$refs.termsandconditionsnotify.closeNotify()
      })
    },
    getOrgUnitsList () {
      this.$store.dispatch('lmsSuperadminOrgUnitsGet', { requestData: this.keyWorkspaceId, preventAddUnit: true })
    },
    getGroupsList () {
      this.$store.dispatch('lmsSuperadminGroupsGet', { requestData: this.keyWorkspaceId, queryUrlParams: this.keyGroupsList.list ? `?filters[ids]=${this.keyGroupsList.join(',')}` : '' })
    }
  },
  mounted () {
    if (this.keyOrganisationUnitsAvailableFor === 'all' || this.keyOrganisationUnitsAvailableFor === 'specific') {
      this.getOrgUnitsList()
    }
    if (this.keyGroupsAvailableFor === 'all' || this.keyGroupsAvailableFor === 'specific') {
      this.getGroupsList()
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/auth/selfregistration/authselfregistrationform";
</style>
